import React, { useEffect, useState } from "react";
import { NewNavbar, BreadCrumbsNew, NewFooter } from "../../components";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";
import { Form, Input, Spin, Alert, Table, message } from 'antd';
import "./index.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Container, Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { post, get } from "../../services/ApiService";
const { TextArea } = Input;

const TokenPrescriptionDetails = () => {
  const { authCode } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [prescription, setPrescription] = useState(null);
  const [patient, setPatient] = useState(null);
  const [signature, setSignature] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [error, setError] = useState(null);
  const [prescriptionUrl, setPrescriptionUrl] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState(null);
  const [dispenseLoading, setDispenseLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, [authCode]);

  const breadcrumbs = [
    { label: "Home", path: "/" },
    { label: "Prescription" },
  ];

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await get(`api/prescriptions/${authCode}`);
      if (res.success) {
        setPrescription(res.prescription);
        setPatient(res.prescription.patientId);
        setSignature(res.prescription.signatureId);
        setDoctor(res.prescription.signatureId.prescriberId);
        setPrescriptionUrl(res.prescriptionUrl);
        setCertificateUrl(res.certificate);
      } else {
        setError("Failed to load prescription data. Please try again.");
      }
    } catch (error) {
      setError("An error occurred while fetching prescription details.");
      console.error("Error fetching prescription:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPrescription = () => {
    if (!prescriptionUrl) {
      message.error("Prescription download URL is not available.");
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = prescriptionUrl;
      link.target = '_blank';
      link.download = `prescription-${authCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success("Prescription download started.");
    } catch (error) {
      console.error("Error downloading prescription:", error);
      message.error("Failed to download prescription. Please try again.");
    }
  };

  const handleSignatureClick = () => {
    if (!certificateUrl) {
      message.error("Certificate URL is not available.");
      return;
    }

    try {
      window.open(certificateUrl, '_blank');
    } catch (error) {
      console.error("Error opening certificate:", error);
      message.error("Failed to open certificate. Please try again.");
    }
  };

  const onDispense = async (values) => {
    setDispenseLoading(true);
    try {
      const dispenseData = {
        prescriptionId: prescription._id,
        token: authCode,
        companyName: values.companyName,
        companyAddress: values.companyAddress,
        pharmacistName: values.pharmacistName,
        gphcNumber: values.gphcNumber,
        dispensedAt: new Date().toISOString()
      };

      const res = await post('api/prescriptions/dispense', dispenseData);
      
      if (res.success) {
        message.success("Prescription marked as dispensed successfully!");
        await fetchData();
        form.resetFields();
      } else {
        message.error(res.error || "Failed to mark prescription as dispensed.");
      }
    } catch (error) {
      console.error("Error dispensing prescription:", error);
      message.error("An error occurred while marking prescription as dispensed.");
    } finally {
      setDispenseLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    if (isNaN(birthDate)) return "";
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatUKDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return 'N/A';
    
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (isNaN(date)) return 'N/A';

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
};

  // Table columns for medications
  const medicationColumns = [
    {
      title: 'Medicine Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text || 'N/A'}</strong>,
    },
    {
      title: 'Strength',
      dataIndex: 'strength',
      key: 'strength',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity, record) => 
        quantity ? `${quantity} ${record.unit || ''}` : 'N/A',
    },
    {
      title: 'Dosage Instructions',
      dataIndex: 'direction',
      key: 'direction',
      render: (text) => text || 'As directed',
    },
    {
      title: 'Form',
      dataIndex: 'formulation',
      key: 'formulation',
      render: (text) => text || 'N/A',
    }
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="weightloss-wraper">
        <div className="breadcrumnb-img-bg pres_bg_img">
          <div className="container">
            <NewNavbar />
            <BreadCrumbsNew breadcrumbs={breadcrumbs} title={"Prescription Details"} />
          </div>
        </div>
        <div className="token_section_details my-5 py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="text-center py-5">
                <Spin size="large" />
                <p className="mt-3">Loading prescription details...</p>
              </div>
            </div>
          </div>
        </div>
        <NewFooter />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="weightloss-wraper">
        <div className="breadcrumnb-img-bg pres_bg_img">
          <div className="container">
            <NewNavbar />
            <BreadCrumbsNew breadcrumbs={breadcrumbs} title={"Prescription Details"} />
          </div>
        </div>
        <div className="token_section_details my-5 py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12">
                <Alert
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                  action={
                    <button 
                      className="btncust w-auto activebg cursor-pointer btncust-light mx-0"
                      onClick={fetchData}
                    >
                      Retry
                    </button>
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <NewFooter />
      </div>
    );
  }

  // No data state
  if (!prescription) {
    return (
      <div className="weightloss-wraper">
        <div className="breadcrumnb-img-bg pres_bg_img">
          <div className="container">
            <NewNavbar />
            <BreadCrumbsNew breadcrumbs={breadcrumbs} title={"Prescription Details"} />
          </div>
        </div>
        <div className="token_section_details my-5 py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12">
                <Alert
                  message="No Data"
                  description="No prescription data found for the provided token."
                  type="warning"
                  showIcon
                />
              </div>
            </div>
          </div>
        </div>
        <NewFooter />
      </div>
    );
  }

  return (
    <div className="weightloss-wraper">
      <div className="breadcrumnb-img-bg pres_bg_img">
        <div className="container">
          <NewNavbar />
          <BreadCrumbsNew breadcrumbs={breadcrumbs} title={"Prescription Details"} />
        </div>
      </div>
      <div className="token_section_details my-5 py-5">
        <div className="container">
          <div className="row justify-content-center">
            <Container className="my-4">
              <Row>
                <Col lg={12}>
                  <Container>
                    <Row>
                      <Col md={12}>
                        <div className="patient_details_notes mb-5">
                          <div className="patient_box_top align-items-center mb-4">
                            <h5 className="title_head text-white">Electronic Prescription</h5>
                            <div className="d-block">
                              <div className="d-flex flex-wrap justify-content-between">
                                <p className="mb-2 w-lg-100 text-center">Token ID: <strong className="metropill">{authCode}</strong></p>
                              </div>
                              {prescription.status === "DISPENSED" && prescription.dispensedAt && (
                                <h5 className="box_dis text-center mt-3">
                                  <b>Already Dispensed on {formatDateTime(prescription.dispensedAt)}</b>
                                </h5>
                              )}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Container>
                  <div className="token_boxes patient_details">
                    <Row>
                      <Col lg={6}>
                        <div className="patient_box align-items-center mb-4">
                          <div className="">
                            <div>
                              <h5 className="mb-4">{doctor.firstName} {doctor.lastName}</h5>
                              <p className="mb-1">My Private Chemist</p>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="patient_box">
                          <h5 className="text-start">Patient Details</h5>
                          <ul>
                            <li><strong>Name:</strong> {patient?.name || 'N/A'}</li>
                            <li><strong>Email:</strong> {patient?.email || 'N/A'}</li>
                            <li>
                              <strong>Age:</strong> 
                              {patient?.dob ? 
                                `${calculateAge(patient.dob)} (${formatUKDate(patient.dob)})` : 
                                patient?.age || 'N/A'
                              }
                            </li>
                            <li><strong>Gender:</strong> {patient?.gender || 'N/A'}</li>
                            <li><strong>Address:</strong> {patient?.address1 || 'N/A'}</li>
                            <li><strong>Phone:</strong> {patient?.phone || 'N/A'}</li>
                          </ul>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <div className="patient_details">
                <Row>
                  <Col md={12}>
                    <div className="patient_box height_auto px-5">
                      <h5 className="text-start mb-4">Medicines</h5>
                      {prescription.drugs && prescription.drugs.length > 0 ? (
                        <Table
                          columns={medicationColumns}
                          dataSource={prescription.drugs.map((medicine, index) => ({
                            key: index,
                            ...medicine
                          }))}
                          pagination={false}
                          size="middle"
                          className="medication-table"
                          locale={{
                            emptyText: 'No medicines prescribed'
                          }}
                        />
                      ) : (
                        <Alert
                          message="No Medications"
                          description="No medicines have been prescribed."
                          type="info"
                          showIcon
                        />
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </Container>

            <div className="btn_doctor pt-5">
              <div className="e_sig">
                <div className="mb-3 text-start d-flex align-items-center justify-content-end gap-3">
                  {["COMPLETE"].includes(prescription?.status)  && (
                    <p className="mb-0 text-danger"><b>Note:</b> This prescription can only be downloaded once. Please ensure you have the necessary software to open PDF files.</p>
                  )}

                  {["READY_FOR_RM"].includes(prescription?.status)  && (
                      <button 
                        className="btn_green btncust w-auto activebg cursor-pointer btncust-lights mx-0"
                        onClick={handleDownloadPrescription}
                      >
                        Download Prescription
                      </button>
                  )}
                </div>
                <h4 className="text-primary mb-4">{doctor.firstName} {doctor.lastName}</h4>
                <p>
                  <b>E-signature ID:</b>{" "}
                  {signature?.signatureId ? (
                    <a 
                      href="#" 
                      onClick={handleSignatureClick}
                      style={{ textDecoration: 'none', cursor: 'pointer' }}
                    >
                      {signature.signatureId}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </p>
                <p>
                  <b>Signed On:</b>{" "}
                  {signature?.createdAt
                    ? formatUKDate(signature.createdAt)
                    : "N/A"}
                </p>

                <p>
                  For e-signature verification or queries contact the practice directly using a number independently derived or leave a message at{" "}
                  <a 
                    href="mailto:info@myprivatechemist.com?subject=E-signature Verification Query&body=Hello, I have a query regarding e-signature verification for prescription token: [TOKEN]"
                    onClick={(e) => {
                      e.preventDefault();
                      const emailBody = `Hello, I have a query regarding e-signature verification for prescription token: ${authCode}`;
                      window.location.href = `mailto:info@myprivatechemist.com?subject=E-signature Verification Query&body=${encodeURIComponent(emailBody)}`;
                    }}
                    style={{ textDecoration: 'none', cursor: 'pointer' }}
                  >
                    My Private Chemist
                  </a>.
                </p>
                {/* {prescription?.status === "DISPENSED" && (
                  <p>This prescription was dispensed on {prescription?.dispensingInfo?.dispensedAt ? formatDateTime(prescription.dispensingInfo.dispensedAt) : 'N/A'} and therefore can no longer be downloaded.</p>
                )} */}
                {prescription?.status === "COMPLETE"  && (
                  <p>This prescription was dispensed on {prescription?.prescriptionDate ? formatDateTime(prescription.prescriptionDate) : 'N/A'} and therefore can no longer be downloaded.</p>
                )}
              </div>
            </div>
            
            {/* Dispense Form - Only show if not already dispensed */}
            {/* {prescription.status !== "DISPENSED" && (
              <div className="prescription_checker token_section mt-5">
                <Form
                  form={form}
                  name="dispenseForm"
                  onFinish={onDispense}
                  layout="vertical"
                  autoComplete="off"
                >
                  <div className="row justify-content-center">
                    <div className="col-12">
                      <h5 className="text-center mb-4">Please complete the form to mark as dispensed</h5>
                    </div>
                    
                    <div className="col-lg-6">
                      <Form.Item
                        label="Company Name"
                        name="companyName"
                        rules={[{ 
                          required: true, 
                          message: "Please enter company name" 
                        }]}
                      >
                        <Input
                          type="text"
                          placeholder="Enter company name"
                          size="large"
                        />
                      </Form.Item>
                    </div>
                    
                    <div className="col-lg-6">
                      <Form.Item
                        label="Pharmacist Name"
                        name="pharmacistName"
                        rules={[{ 
                          required: true, 
                          message: "Please enter pharmacist name" 
                        }]}
                      >
                        <Input
                          type="text"
                          placeholder="Enter pharmacist name"
                          size="large"
                        />
                      </Form.Item>
                    </div>
                    
                    <div className="col-lg-6">
                      <Form.Item
                        label="GPHC Number"
                        name="gphcNumber"
                        rules={[{ 
                          required: true, 
                          message: "Please enter GPHC number" 
                        }]}
                      >
                        <Input
                          type="text"
                          placeholder="Enter GPHC number"
                          size="large"
                        />
                      </Form.Item>
                    </div>
                    
                    <div className="col-lg-6">
                      <Form.Item
                        label="Token"
                        name="token"
                        initialValue={authCode}
                      >
                        <Input
                          type="text"
                          value={authCode}
                          disabled
                          size="large"
                        />
                      </Form.Item>
                    </div>
                    
                    <div className="col-12">
                      <Form.Item
                        label="Company Address"
                        name="companyAddress"
                        rules={[{ 
                          required: true, 
                          message: "Please enter company address" 
                        }]}
                      >
                        <TextArea 
                          rows={4} 
                          placeholder="Enter full company address" 
                          size="large"
                        />
                      </Form.Item>
                    </div>

                    <div className="col-lg-12 mb-10 text-center">
                      <button
                        type="submit"
                        className="btncust w-auto activebg cursor-pointer btncust-light mx-0"
                        disabled={dispenseLoading}
                      >
                        {dispenseLoading ? (
                          <>
                            <Spin size="small" className="mr-2" />
                            Processing...
                          </>
                        ) : (
                          "Mark as Dispensed"
                        )}
                      </button>
                    </div>
                  </div>
                </Form>
              </div>
            )} */}
          </div>
        </div>
      </div>
      <NewFooter />
    </div>
  );
};

export { TokenPrescriptionDetails };